import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '../../../Components/UI/dialog';
import { Button } from '../../../Components/UI/button';
import { Input } from '../../../Components/UI/input';
import { Label } from '../../../Components/UI/label';
import { X, ArrowLeft, Save, Check } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { updateCompanyProfile, sendRemindPINAPI } from './ProfileSectionAPI';
import { toast } from 'sonner';
import { setGlobalLoading } from '../../../Store/Actions';
import { getUserData } from '../../../Utils/Storage';
import CustomSearchableDropdown from '../../../Components/Common/CustomSearchableDD';
import { states } from '../../../Components/Enums/DataEnums';
import { validateTaxID } from '../../../Utils/Utilities';
import { useRef } from 'react';

// Configuration for tax ID validation
const taxConfigData = {
  India: {
    countryID: 1,
    minLength: 15,
    maxLength: 15,
    label: 'GSTIN',
    placeholder: 'e.g. 24XXXXX0000X1XX',
  },
  Other: {
    minLength: 13,
    maxLength: 18,
    label: 'Tax ID',
    placeholder: 'e.g. 9XX-7X-XXXX',
  }
};

const ProfileSection = ({
  open,
  onClose,
  onSuccess,
  requiredFields = false,
  isUpgradeFlow = false, // New prop to skip PIN
}) => {
  // Form data structure
  const initialFormData = {
    companyName: '',
    pocUserID: 0,
    taxID: '',
    stateID: 0,
    stateName: '',
    city: '',
    pin: ''
  };

  const [formData, setFormData] = useState(initialFormData);
  const [hasResent, setHasResent] = useState(false);
  const agencyData = useSelector((state) => state.agencyData.data);
  const countryID = agencyData.CountryID;

  const dispatch = useDispatch();

  // Check if current country is India
  const isIndia = countryID === taxConfigData.India.countryID;
  const taxConfig = isIndia ? taxConfigData.India : taxConfigData.Other;
  const isFormInitialized = useRef(false);

  // Memoize users data to prevent unnecessary re-renders
  const users = useMemo(() => getUserData(), []);

  // Prepare user options for dropdown
  const userOptions = useMemo(() =>
    users.map(user => ({
      value: user.UserID,
      label: user.Name
    })), [users]
  );

  // Prepare state options for dropdown (only for India)
  const stateOptions = useMemo(() =>
    states
      .filter(state => state.CountryID === 1) // Only Indian states
      .map(state => ({
        value: state.StateID,
        label: state.State,
        gstPrefix: state.GSTPrefix
      })), []
  );

  useEffect(() => {
    if (!open) {
      setFormData(initialFormData);
      setHasResent(false);
      isFormInitialized.current = false; // reset when dialog closes
      return;
    }

    // ✅ Only initialize ONCE when dialog opens
    if (open && agencyData && users.length > 0 && !isFormInitialized.current) {
      const newData = { ...initialFormData };

      newData.companyName = agencyData.AgencyName || '';
      newData.pocUserID = agencyData.POCUserID || agencyData.AdminUserID || 0;
      newData.taxID = agencyData.TaxID || '';
      newData.city = agencyData.City || '';
      newData.pin = '';

      if (agencyData.StateID) {
        newData.stateID = agencyData.StateID;
        if (isIndia) {
          const selectedState = states.find(
            state => state.StateID === agencyData.StateID
          );
          newData.stateName = selectedState ? selectedState.State : '';
        } else {
          newData.stateName = agencyData.StateName || '';
        }
      }

      setFormData(newData);
      isFormInitialized.current = true; // 🔥 important
    }
  }, [open, agencyData, users.length]);

  const handleClose = () => {
    setFormData(initialFormData); // Clear state locally
    onClose();
  };

  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      onClose();
    }
  };

  // Handle form field changes
  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  // Handle Resend PIN
  const handleResendPIN = async () => {
    if (hasResent) return;
    dispatch(setGlobalLoading(true));
    try {
      const response = await sendRemindPINAPI(agencyData.Mobile, agencyData.CC || '91');
      if (response.error === false) {
        setHasResent(true);
        toast.success(response.message || 'PIN sent to your registered mobile number.');
      } else {
        toast.error(response.message || 'Failed to send PIN.');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  // Handle POC user selection
  const handlePOCUserChange = (userId) => {
    handleFieldChange('pocUserID', userId);
  };

  // Handle state selection (for India)
  const handleStateChange = (stateId) => {
    if (!stateId) { // Check for null, undefined, or 0
      setFormData(prev => ({
        ...prev,
        stateID: 0,
        stateName: ''
      }));
      return;
    }
    const selectedState = states.find(state => state.StateID === stateId);
    if (selectedState) {
      setFormData(prev => ({
        ...prev,
        stateID: selectedState.StateID,
        stateName: selectedState.State
      }));
    }
  };

  // Handle state name change (for non-India countries)
  const handleStateNameChange = (stateName) => {
    setFormData(prev => ({
      ...prev,
      stateName: stateName,
      stateID: 0 // Reset stateID for non-India countries
    }));
  };

  // Get state name from GSTIN for display
  const changeStateSelection = () => {
    if (!isIndia || !formData.taxID || formData.taxID.length < 2) return;

    const gstPrefix = formData.taxID.substring(0, 2);
    const matchingState = states.find(state =>
      state.CountryID === 1 && state.GSTPrefix === gstPrefix
    );

    if (matchingState) {
      setFormData(prev => ({
        ...prev,
        stateID: matchingState.StateID,
        stateName: matchingState.State
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        stateID: 0,
        stateName: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = [];

    // Company name validation (Required)
    if (!formData.companyName.trim()) {
      errors.push('Business name is required.');
    }

    // POC user validation (Required)
    if (!formData.pocUserID || formData.pocUserID === 0) {
      errors.push('Point of contact is required.');
    }
    // PIN validation (Required only if not in upgrade flow)
    if (!isUpgradeFlow && !formData.pin.trim()) {
      errors.push('PIN is required.');
    }

    // City validation (Required if requiredFields is true)
    if (requiredFields && !formData.city.trim()) {
      errors.push('Please enter a city.');
    }

    // State validation (Required if requiredFields is true)
    if (requiredFields) {
      if (isIndia && (!formData.stateID || formData.stateID === 0)) {
        errors.push('Please enter a state.');
      } else if (!isIndia && !formData.stateName.trim()) {
        errors.push('Please enter a state/province.');
      }
    }

    // Tax ID validation (Optional - only validate if provided)
    if (formData.taxID.trim()) {
      if (isIndia) {
        // For India, validate GSTIN
        const selectedState = states.find(state => state.StateID === formData.stateID);
        const gstPrefix = selectedState ? selectedState.GSTPrefix : '';
        const validation = validateTaxID(formData.taxID, gstPrefix, true, taxConfig.minLength, taxConfig.maxLength);
        if (!validation.isValid) {
          errors.push(validation.message);
        }
      } else {
        // For Other countries, validate Tax ID
        const validation = validateTaxID(formData.taxID, '', false, taxConfig.minLength, taxConfig.maxLength);
        if (!validation.isValid) {
          errors.push(validation.message);
        }
      }
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async () => {
    const errors = validateForm();

    if (errors.length > 0) {
      toast.error(errors[0]); // Show first error
      return;
    }

    dispatch(setGlobalLoading(true));

    try {
      // Prepare API payload
      const apiPayload = {
        companyName: formData.companyName.trim(),
        pocUserID: formData.pocUserID,
        gstin: formData.taxID.replace(/\s/g, ''), // Remove spaces from tax ID
        city: formData.city.trim(),
        pin: formData.pin.trim(),
        taxID: formData.taxID.trim(),
        stateID: formData.stateID,
        stateName: formData.stateName.trim()
      };

      // Use 'UPGPROF' changeType to bypass PIN in backend during upgrade
      const result = await updateCompanyProfile(isUpgradeFlow ? 'UPGPROF' : 'PROFSET', apiPayload);
      if (result.error === false) {
        toast.success('Profile updated.');
        if (onSuccess) {
          onSuccess({ city: formData.city, stateID: formData.stateID });
        } else {
          handleClose();
        }
      } else {
        setFormData(prev => ({
          ...prev,
          pin: ''
        }));
        toast.error(result.message || 'Incorrect PIN. Please try again.');
      }

    } catch (error) {
      toast.error('Failed to update profile. Please try again.');
      console.error('Error saving profile:', error);
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange} >
      <DialogContent className="lg:!max-w-[35vw] max-w-[100vw]">
        <DialogHeader className="gap-0 px-3 sm:px-4 md:px-4">
          <div className="sm:hidden flex items-center justify-between w-full h-8">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <DialogClose tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center" onClick={handleClose}>
                <ArrowLeft className="h-6 w-6" />
                <span className="sr-only">Back</span>
              </DialogClose>
              <DialogTitle className="text-lg font-medium">
                <div className="truncate">Profile Setup</div>
              </DialogTitle>
            </div>
          </div>
          <DialogTitle className="hidden sm:block text-lg md:text-xl">
            Profile Setup
          </DialogTitle>
          <div className="hidden sm:flex items-center gap-2">
            <DialogClose className="flex-shrink-0" tabIndex={-1} onClick={handleClose}>
              <X className="h-5 w-5" />
              <span className="sr-only">Close</span>
            </DialogClose>
          </div>
        </DialogHeader>

        {/* {requiredFields && (
          <div className="mx-4">
            <p className="text-sm text-sahi-dark-blue font-medium">
              Note: Complete your profile to continue your subscription.
            </p>
          </div>
        )} */}

        {/* Scrollable middle section */}
        <div className="lg:max-h-[70vh] max-h-[90vh] overflow-y-auto px-4">
          {/* Company Name Field - Full Width */}
          <div className="space-y-2 mb-4">
            <Label htmlFor="companyName" className="text-sm font-medium" required={true}>
              Business Name
            </Label>
            <Input
              id="companyName"
              type="text"
              placeholder="e.g. SahiClub Pvt. Ltd.."
              value={formData.companyName}
              onChange={(e) => handleFieldChange('companyName', e.target.value)}
              className="w-full sahi-input"
              autoComplete="off"
            />
          </div>

          {/* Third Row: State and City */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            {/* City Field */}
            <div className="space-y-2">
              <Label htmlFor="city" className="text-sm font-medium" required={requiredFields}>
                City
              </Label>
              <Input
                id="city"
                type="text"
                placeholder="e.g. Gandhinagar"
                value={formData.city}
                onChange={(e) => handleFieldChange('city', e.target.value)}
                className="w-full sahi-input"
                autoComplete="off"
              />
            </div>
            {/* State Field */}
            <div className="space-y-2">
              {isIndia ? (
                <>
                  <Label htmlFor="state" className="text-sm font-medium" required={requiredFields}>
                    State
                  </Label>
                  <CustomSearchableDropdown
                    fieldID="state"
                    required={true}
                    label=""
                    placeholder="Select"
                    options={stateOptions}
                    value={formData.stateID}
                    onChange={handleStateChange}
                    emptyMessage={<>No states available.</>}
                    onSelect={handleStateChange}
                    searchEnabled={true}
                    disabled={false}
                  />
                </>
              ) : (
                <>
                  <Label htmlFor="state" className="text-sm font-medium" required={requiredFields}>
                    State/Province
                  </Label>
                  <Input
                    id="state"
                    type="text"
                    placeholder="Select"
                    value={formData.stateName}
                    onChange={(e) => handleStateNameChange(e.target.value)}
                    className="w-full sahi-input"
                    autoComplete="off"
                  />
                </>
              )}
            </div>
          </div>

          {/* Second Row: Point of Contact and Tax ID */}
          <div className={`grid grid-cols-1 md:grid-cols-1 ${!isUpgradeFlow ? 'mb-4' : ''}`}>
            {/* POC User Field */}
            <div className="space-y-2 hidden">
              <Label htmlFor="pocUser" className="text-sm font-medium" required={true}>
                Point of Contact (Display on WebStore)
              </Label>
              <CustomSearchableDropdown
                fieldID="pocUser"
                required={true}
                label=""
                placeholder="Select Point of Contact"
                options={userOptions}
                value={formData.pocUserID}
                onChange={handlePOCUserChange}
                emptyMessage={<>No users available.</>}
                onSelect={handlePOCUserChange}
                searchEnabled={true}
                disabled={false}
              />
            </div>

            {/* Tax ID Field */}
            <div className="space-y-2 w-full">
              <Label htmlFor="taxID" className="text-sm font-medium">
                {taxConfig.label}
              </Label>
              <Input
                id="taxID"
                type="text"
                placeholder={taxConfig.placeholder}
                value={formData.taxID}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length >= taxConfig.minLength && value.length <= taxConfig.maxLength) {
                    handleFieldChange('taxID', value);
                  } else if (value.length < taxConfig.minLength) {
                    handleFieldChange('taxID', value);
                  } else if (value.length > taxConfig.maxLength) {
                    handleFieldChange('taxID', value.substring(0, taxConfig.maxLength));
                  }
                }}
                onBlur={changeStateSelection}
                className="sahi-input"
                autoComplete="new-password"
                maxLength={taxConfig.maxLength}
                autoCorrect="off"
                spellCheck="false"
                data-lpignore="true"
                data-form-type="other"
              />
            </div>
          </div>

          {/* PIN Field - Hide if in upgrade flow */}
          {!isUpgradeFlow && <div className={`w-48 md:w-full md:grid md:grid-cols-2 space-y-2 gap-4 `}>
            <div className=''>
              <Label htmlFor="pin" className="text-sm font-medium mb-1" required={true}>
                PIN for Verification
              </Label>
              <div className="flex gap-2 items-center">
                <Input
                  id="pin"
                  type="password"
                  placeholder=""
                  value={formData.pin}
                  onChange={(e) => {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    handleFieldChange('pin', numericValue);
                  }}
                  className="w-full sahi-input"
                  style={{ width: 'calc(100% - 80px)' }}
                  autoComplete="off"
                  inputMode="numeric"
                  maxLength={4}
                />
                <div className="w-[80px] items-center">
                  {!hasResent && (
                    <Button
                      variant="secondary"
                      onClick={handleResendPIN}
                      disabled={false}
                      className="w-full whitespace-nowrap px-2"
                      type="button"
                    >
                      Remind
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>}
        </div>

        {/* Footer with buttons */}
        <div className="flex items-center justify-start gap-3 lg:px-4 px-4 lg:pb-4 pb-4 pt-0">
          <Button
            onClick={handleSubmit}
            className="w-auto"
          >
            <Check className="h-4 w-4" />
            Save Profile
          </Button>
          <Button
            variant="secondary"
            onClick={handleClose}
          >
            <X className="w-3 h-3" />
            Close
          </Button>
        </div>

      </DialogContent>
    </Dialog>
  );
};

export default ProfileSection;